import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle, ArrowRight, Building2, Eye, EyeOff, LockKeyhole, Mail, ShieldCheck } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';

function LoginPageRBAC() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(formData.email, formData.password);

      if (result.success) {
        // Redirect based on role
        const roleRedirects = {
          super_admin: '/super-admin',
          admin: '/dashboard',
          staff: '/employee-dashboard',
        };
        navigate(roleRedirects[result.user.role] || from, { replace: true });
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <main className="min-h-screen bg-slate-50 lg:grid lg:grid-cols-[minmax(0,1.08fr)_minmax(500px,0.92fr)]">
      {/* Left: staff image */}
      <section className="relative hidden min-h-screen overflow-hidden bg-slate-950 lg:block">
        <img
          src="/hotel-staff.png"
          alt="Hotel professional welcoming guests at reception"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/15 to-slate-950/25" />
        <div className="relative flex h-full min-h-screen flex-col justify-between p-12 xl:p-16">
          <div className="flex items-center justify-end gap-3 text-right text-white">
            <div>
              <p className="text-lg font-semibold tracking-tight">Zaff Hotel Platform</p>
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-white/65">Property operations</p>
            </div>
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 shadow-lg shadow-blue-950/20">
              <Building2 aria-hidden="true" className="h-6 w-6" />
            </span>
          </div>

          <div className="ml-auto max-w-sm text-white xl:max-w-md">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3.5 py-2 text-sm font-medium backdrop-blur-md">
              <ShieldCheck aria-hidden="true" className="h-4 w-4 text-blue-300" />
              Secure workspace for hotel teams
            </div>
            <h1 className="text-4xl font-semibold leading-tight tracking-tight xl:text-5xl">
              እንኳን ደህና መጡ, የእኛ ሲስተም የሆቴል አስተዳደሮን ይለውጠዋል።
            </h1>
            <p className="mt-5 max-w-lg text-base leading-7 text-slate-200">
              Manage daily operations, staff, guests, and performance from one connected platform built for modern hotels.
            </p>
            <div className="mt-10 flex items-center gap-4 border-t border-white/20 pt-6 text-sm text-slate-300">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              Reliable access for every shift, every day
            </div>
          </div>
        </div>
      </section>

      {/* Right: login form */}
      <section className="flex min-h-screen flex-col bg-white">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-5 sm:px-10 lg:border-none lg:px-12">
          <div className="flex items-center gap-2.5 lg:hidden">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 text-white">
              <Building2 aria-hidden="true" className="h-5 w-5" />
            </span>
            <span className="font-semibold text-slate-900">Zaff Hotel Platform</span>
          </div>
          <p className="ml-auto text-sm text-slate-500">
            Secure access for authorized hotel teams
          </p>
        </div>

        <div className="flex flex-1 items-center justify-center px-6 py-12 sm:px-10 lg:px-12">
          <div className="w-full max-w-[430px]">
            {/* Header */}
            <div className="mb-9">
              <p className="mb-3 text-sm font-semibold text-blue-600">WELCOME BACK</p>
              <h2 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">Sign in to your workspace</h2>
              <p className="mt-3 text-base leading-6 text-slate-500">Enter your credentials to continue to hotel operations.</p>
            </div>

            {/* Error Message */}
            {error && (
              <div role="alert" className="mb-6 flex gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                <AlertCircle aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0" />
                <p>{error}</p>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <label htmlFor="email" className="mb-2 block text-sm font-semibold text-slate-700">Email address</label>
                <div className="relative">
                  <Mail aria-hidden="true" className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <input
                    id="email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="name@yourhotel.com"
                    className="h-12 w-full rounded-xl border border-slate-300 bg-white pl-11 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <div className="mb-2 flex items-center justify-between gap-4">
                  <label htmlFor="password" className="text-sm font-semibold text-slate-700">Password</label>
                  <Link to="/forgot-password" className="text-sm font-semibold text-blue-600 transition-colors hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">Forgot password?</Link>
                </div>
                <div className="relative">
                  <LockKeyhole aria-hidden="true" className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="Enter your password"
                    className="h-12 w-full rounded-xl border border-slate-300 bg-white pl-11 pr-12 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((visible) => !visible)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-1.5 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {showPassword ? <EyeOff aria-hidden="true" className="h-5 w-5" /> : <Eye aria-hidden="true" className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="group flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-500/25 disabled:cursor-not-allowed disabled:bg-slate-400"
              >
                {loading ? 'Signing in…' : 'Sign in securely'}
                {!loading && <ArrowRight aria-hidden="true" className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
              </button>
            </form>

            {/* Footer */}
            <div className="mt-8 border-t border-slate-200 pt-6 text-center">
              <p className="text-sm text-slate-500">
                New to Zaff?{' '}
                <Link to="/register" className="font-semibold text-blue-600 transition-colors hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">Create an account</Link>
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 py-6 text-center text-xs text-slate-400">
          © {new Date().getFullYear()} Zaff Software. All rights reserved.
        </div>
      </section>
    </main>
  );
}

export default LoginPageRBAC;
